import React from 'react';
import classNames from 'classnames';
import styled from '@emotion/styled';
import { token } from '@atlaskit/tokens';
import { Inline } from '@atlaskit/primitives';
import mimeTypes from 'mime-types';
import filesize from 'filesize';
import escapeStringRegexp from 'escape-string-regexp';
import { sanitize } from 'dompurify';
import { Attachment, EmailAddress } from '@atlassian/cssxp-types-email';
import { partnerEmailAttachmentHandler } from '@atlassian/temp-proxies';
import Badge from '@atlaskit/badge';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { FormattedDate } from '../formatted';
//@ts-ignore
import PdfIcon from '../assets/img/application/pdf.png';
import { SummaryPanelContainer, SummaryPanelIcon, SummaryPanelRow } from '../expandable-summary-container';
import { useNotification } from '../hooks';
import { errorNotificationBuilder, NotificationTypes } from '../notifications';
import './email-details.scss';

interface Props {
  emailData: any;
  attachmentRoute?: string;
  errorMessage?: any;
  version?: number;
  isEmailsImprovementEnabled?: boolean;
}

const EmailBodyContainer = styled.div`
  &.email-body-container-v2 {
    border-radius: ${token('space.100')};
    border: 1px solid ${token('color.border')};
    background: none;
    margin-bottom: ${token('space.400')};
    .email-body {
      box-shadow: none;
    }
  }
`;

const Address = ({ address, name, addonContent }: { address: string; name?: string; addonContent?: JSX.Element }) => (
  <div>
    {name ? `${name} (${address})` : address} {addonContent}
  </div>
);

interface AddressFieldProps {
  addresses: EmailAddress[];
  isVersion2?: boolean;
}

const SHOW_MORE_THRESHOLD = 5;

const ShowMoreAddress = (props: AddressFieldProps) => {
  const [showMore, setShowMore] = React.useState<boolean>(false);
  const { addresses, isVersion2 } = props;
  const totalAddresses = addresses.length;
  if (totalAddresses === 0) return null;
  const firstAddress = addresses[0];
  const { address, name } = firstAddress;
  const hasSingleToAddress = totalAddresses === 1;

  const showMoreButton = (
    <a
      onClick={() => setShowMore((prevShowMore) => !prevShowMore)}
      className="show-more-email"
      data-testid="show-more-email"
    >
      {!showMore && <span>{isVersion2 ? 'Show more' : `+${totalAddresses - 1} more`}</span>}
      {showMore && <span>{isVersion2 ? 'Show less' : 'Hide'}</span>}
    </a>
  );

  const [, ...remainingAddresses] = addresses;

  if (isVersion2) {
    const showMoreBadgeContent = showMore ? '-' : `+${totalAddresses - SHOW_MORE_THRESHOLD}`;
    const batch1Length = totalAddresses > SHOW_MORE_THRESHOLD ? SHOW_MORE_THRESHOLD : totalAddresses;
    const addressesBatch1 = Array.from({ length: batch1Length }, (_, index) => {
      const { address, name } = addresses[index];

      return <Address key={address} name={name} address={address} />;
    });
    const remainingAddressesBatch = addresses.slice(batch1Length);
    const addressBatch2 = remainingAddressesBatch.map(({ address, name }) => (
      <Address key={address} name={name} address={address} />
    ));

    return (
      <div className="address-container">
        <Inline separator=",&nbsp;&nbsp;" shouldWrap>
          {addressesBatch1}
          {showMore && addressBatch2}
        </Inline>
        {totalAddresses > SHOW_MORE_THRESHOLD && (
          <div className="flex align-center gap2">
            <Badge>{showMoreBadgeContent}</Badge>
            {showMoreButton}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <Address
        key={address}
        name={name}
        address={address}
        addonContent={!hasSingleToAddress ? <>&nbsp; {showMoreButton}</> : undefined}
      />
      {showMore && <AddressField addresses={remainingAddresses} />}
    </div>
  );
};

const AddressField = ({ addresses }: AddressFieldProps) => (
  <div>
    {addresses.map(({ address = '', name }) => (
      <Address key={address} name={name} address={address} />
    ))}
  </div>
);

const getEmailAttachmentUrl = async (id: string) => {
  return await partnerEmailAttachmentHandler(id);
};

const EmailDetails = ({ emailData, errorMessage, version, isEmailsImprovementEnabled }: Props) => {
  const [attachmentUrlLinkMap, setAttachmentUrlLinkMap] = React.useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const { sendErrorNotification, notificationSent, setNotificationSent } = useNotification();
  const isAttachmentDownloadable = (attachment: Attachment) => attachment.contentDisposition === 'attachment';
  const isAttachmentInline = (attachment: Attachment) => attachment.contentDisposition === 'inline';
  const EMBEDDED_EMAIL_MIME_TYPE = 'message/rfc822';
  const isVersion2 = version === 2;

  const getAttachmentLink = (attachmentId: string) => `/api/1/emails/attachments/${attachmentId}`;

  const getAttachmentFilename = (attachment: Attachment) => {
    if (attachment.filename) {
      return attachment.filename;
    }

    const defaultFilename = attachment.contentType === EMBEDDED_EMAIL_MIME_TYPE ? 'message' : 'attachment';
    const ext = mimeTypes.extension(attachment.contentType) || 'unknown';

    return `${defaultFilename}.${ext}`;
  };

  /**
   * The function transformHtmlBody takes in an HTML string and an array of attachments, and replaces any
   * instances of inline attachment content IDs in the HTML string with a link to the corresponding attachment.
   */
  const transformHtmlBody = (html: string, inlineAttachments: Attachment[]) => {
    let transformedHtml = html;

    inlineAttachments.forEach(({ contentId = '', id }) => {
      if (contentId.startsWith('<')) {
        contentId = contentId.substring(1, contentId.length - 1);
      }

      const cidPattern = new RegExp(escapeStringRegexp(`cid:${contentId}`), 'g');
      transformedHtml = transformedHtml.replace(cidPattern, getAttachmentLink(id));
    });

    return transformedHtml;
  };

  /** This function replaces /n to html corresponding <br> **/
  const transformTextBody = (text: string) => text.replace(/\n/g, '<br>');

  const renderHeaderField = (fieldName: string, content: React.ReactNode) => {
    const fieldIdentifier = `email-header-field-${fieldName.toLowerCase().replace(/\s/g, '-')}`;

    return <SummaryPanelRow testId={fieldIdentifier} label={fieldName} value={content} />;
  };

  const downloadableAttachments = emailData.attachments?.filter(isAttachmentDownloadable) || [];

  const attachmentIds = downloadableAttachments.map(({ id }: { id: string }) => id);

  React.useEffect(() => {
    const fetchAttachmentUrls = async () => {
      try {
        setIsLoading(true);
        const attachmentUrlLinkMap: Record<string, string> = {};
        const attachmentsUrls = await Promise.all(attachmentIds.map(getEmailAttachmentUrl));
        for (const index of Object.keys({ ...attachmentsUrls })) {
          //@ts-ignore
          attachmentUrlLinkMap[attachmentIds[index]] = attachmentsUrls[index];
        }
        setAttachmentUrlLinkMap(attachmentUrlLinkMap);
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        if (!notificationSent) {
          const notificationOptions = errorNotificationBuilder()
            .withId('signed-url-api-fails')
            .withHeader("We couldn't process the attachments")
            .withMessage(errorMessage())
            .withDismissAfter(5000)
            .isVersion(NotificationTypes.FLAG)
            .build();
          sendErrorNotification(notificationOptions);
          setNotificationSent(true);
        }
      }
    };
    fetchAttachmentUrls();
  }, []);

  const renderAttachments = () => {
    const downloadableAttachments = emailData.attachments?.filter(isAttachmentDownloadable) || [];

    return downloadableAttachments.map((attachment: Attachment) => {
      const { id, size } = attachment;
      const filename = getAttachmentFilename(attachment);

      return (
        <div key={id} className="attachment-item" data-testid="attachment-item">
          <img src={PdfIcon} className="avatar medium not-round" alt="pdf-icon" />
          {isLoading ? (
            <SkeletonLoaderWrapper>
              <SkeletonLoader />
            </SkeletonLoaderWrapper>
          ) : attachmentUrlLinkMap[id] ? (
            <>
              <a
                href={attachmentUrlLinkMap[id]}
                target="_blank"
                rel="noopener noreferrer"
                data-testId="attachment-item-link"
              >{`${filename} (${filesize(size, {
                round: 0,
              })})`}</a>
            </>
          ) : (
            <p>{`${filename} (${filesize(size, {
              round: 0,
            })})`}</p>
          )}
        </div>
      );
    });
  };

  const {
    subject = 'No subject',
    date,
    from,
    to,
    cc,
    replyTo,
    html,
    text = 'No e-mail body',
    attachments = [],
  } = emailData;
  const inlineAttachments = attachments.filter(isAttachmentInline);
  const body = html !== undefined ? transformHtmlBody(html, inlineAttachments) : transformTextBody(text);

  return (
    <div>
      <div
        className={classNames('preview-panel email-details-container', { 'email-details-container-v2': isVersion2 })}
      >
        <div className="email-header">
          <section>
            <SummaryPanelContainer
              title={!isVersion2 ? 'Email details' : ''}
              iconKey={!isVersion2 ? SummaryPanelIcon.EMAIL_ICON : ''}
            >
              {from && renderHeaderField('From', <AddressField addresses={from} />)}
              {isEmailsImprovementEnabled &&
                to &&
                renderHeaderField('To', <ShowMoreAddress addresses={to} isVersion2={isVersion2} />)}
              {isEmailsImprovementEnabled &&
                cc &&
                renderHeaderField('CC', <ShowMoreAddress addresses={cc} isVersion2={isVersion2} />)}
              {!isVersion2 && replyTo && renderHeaderField('Reply to', <AddressField addresses={replyTo} />)}
              {date && renderHeaderField('Date', <FormattedDate date={date} showTime={true} />)}
              {isVersion2 && subject && renderHeaderField('Subject', subject)}
            </SummaryPanelContainer>
          </section>
        </div>
        <EmailBodyContainer className={classNames('email-body-container', { 'email-body-container-v2': isVersion2 })}>
          <div className="email-body">
            {!isVersion2 && <h3 className="email-subject">{subject}</h3>}
            {renderAttachments()}
            <div data-testid="email-body" dangerouslySetInnerHTML={{ __html: sanitize(body) }} />
          </div>
        </EmailBodyContainer>
      </div>
    </div>
  );
};

export default EmailDetails;
